import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Box,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  Send,
  Schedule,
  Image,
  VideoLibrary,
  AutoAwesome,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { socialMediaApi } from '../../api/socialMediaApi';

interface ContentGeneratorProps {
  onAlert: (type: 'success' | 'error' | 'info', message: string) => void;
}

const ContentGenerator: React.FC<ContentGeneratorProps> = ({ onAlert }) => {
  const [contentType, setContentType] = useState('daily-reveal');
  const [platforms, setPlatforms] = useState({
    twitter: true,
    facebook: true,
    instagram: true,
    tiktok: false,
  });
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [scheduledTime, setScheduledTime] = useState<dayjs.Dayjs | null>(null);
  const [customText, setCustomText] = useState('');
  const [useCustomText, setUseCustomText] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateOption, setGenerateOption] = useState<'now' | 'schedule'>('now');

  const handlePlatformChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPlatforms({
      ...platforms,
      [event.target.name]: event.target.checked,
    });
  };

  const handleGenerate = async () => {
    const selectedPlatforms = Object.keys(platforms).filter(p => platforms[p as keyof typeof platforms]);

    if (selectedPlatforms.length === 0) {
      onAlert('error', 'Please select at least one platform');
      return;
    }

    if (generateOption === 'schedule' && !scheduledTime) {
      onAlert('error', 'Please select a scheduled time');
      return;
    }

    try {
      setGenerating(true);

      const payload = {
        contentType,
        platforms: selectedPlatforms,
        requiresApproval,
        scheduledTime: generateOption === 'schedule' ? scheduledTime?.valueOf() : undefined,
        customText: useCustomText ? customText : undefined,
      };

      const response = await socialMediaApi.generateContent(payload);

      const message = requiresApproval
        ? `Content generated for ${selectedPlatforms.length} platform(s). Pending approval.`
        : `Content generated and ${generateOption === 'schedule' ? 'scheduled' : 'posted'} for ${selectedPlatforms.length} platform(s).`;

      onAlert('success', message);

      // Reset form
      setCustomText('');
      setScheduledTime(null);
    } catch (error) {
      onAlert('error', 'Failed to generate content');
      console.error('Error generating content:', error);
    } finally {
      setGenerating(false);
    }
  };

  const getContentTypeDescription = (type: string) => {
    switch (type) {
      case 'daily-reveal':
        return 'Reveal yesterday\'s answer with game statistics';
      case 'carousel':
        return '10-15 slide carousel with hints and reveal';
      case 'leaderboard':
        return 'Top players and achievements from yesterday';
      case 'teaser':
        return 'Hints about today\'s game to drive traffic';
      default:
        return '';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Content Type
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                >
                  <FormControlLabel
                    value="daily-reveal"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Daily Reveal</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getContentTypeDescription('daily-reveal')}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="carousel"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Interactive Carousel</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getContentTypeDescription('carousel')}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="leaderboard"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Leaderboard Highlights</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getContentTypeDescription('leaderboard')}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="teaser"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Today's Teaser</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getContentTypeDescription('teaser')}
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Platforms
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={platforms.twitter}
                      onChange={handlePlatformChange}
                      name="twitter"
                    />
                  }
                  label="Twitter/X"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={platforms.facebook}
                      onChange={handlePlatformChange}
                      name="facebook"
                    />
                  }
                  label="Facebook"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={platforms.instagram}
                      onChange={handlePlatformChange}
                      name="instagram"
                    />
                  }
                  label="Instagram"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={platforms.tiktok}
                      onChange={handlePlatformChange}
                      name="tiktok"
                      disabled={contentType !== 'carousel' && contentType !== 'daily-reveal'}
                    />
                  }
                  label="TikTok (Video)"
                />
              </FormGroup>

              {platforms.tiktok && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  TikTok will generate a 15-second video version
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Options
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useCustomText}
                        onChange={(e) => setUseCustomText(e.target.checked)}
                      />
                    }
                    label="Use custom text"
                  />
                </Grid>

                {useCustomText && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Custom text (will be adapted per platform)"
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={requiresApproval}
                        onChange={(e) => setRequiresApproval(e.target.checked)}
                      />
                    }
                    label="Require approval before posting"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Posting Time</FormLabel>
                    <RadioGroup
                      row
                      value={generateOption}
                      onChange={(e) => setGenerateOption(e.target.value as 'now' | 'schedule')}
                    >
                      <FormControlLabel
                        value="now"
                        control={<Radio />}
                        label="Generate now"
                      />
                      <FormControlLabel
                        value="schedule"
                        control={<Radio />}
                        label="Schedule for later"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {generateOption === 'schedule' && (
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label="Scheduled time"
                      value={scheduledTime}
                      onChange={setScheduledTime}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={generating ? <CircularProgress size={20} /> : <AutoAwesome />}
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Generate Content'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default ContentGenerator;